import { useEffect, useMemo, useState } from 'react';
import { Link, Route, Routes, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MapPin, ArrowSquareOut, FilePlus, PaperPlaneTilt } from '@phosphor-icons/react';
import { ApiError, api } from '@/lib/api';
import {
  DISTRESS_FLAGS,
  FLAG_LABELS,
  STATUS_LABELS,
  type Campaign,
  type PropertyListItem,
  type PropertyListResponse,
  type PropertyStatus,
} from '@/lib/types';
import { cn } from '@/lib/utils';
import PropertyDetail from './PropertyDetail';
import KatastrUploadModal from '@/components/KatastrUploadModal';

const STATUS_FILTERS: { value: '' | PropertyStatus; label: string }[] = [
  { value: '', label: 'Vše' },
  { value: 'unclassified', label: 'Nezatříděné' },
  { value: 'katastr_loaded', label: 'Katastr nahraný' },
  { value: 'ready', label: 'Připravené' },
  { value: 'archived', label: 'Archiv' },
];

const LETTER_AGE_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'Vše' },
  { value: 'none', label: 'Bez dopisu' },
  { value: '7d', label: '> týden' },
  { value: '14d', label: '> 14 dnů' },
  { value: '1m', label: '> měsíc' },
  { value: '2m', label: '> 2 měsíce' },
];

function mapyUrl(lat: number, lon: number): string {
  // Letecká mapa, zoom 19, info panel napravo otevřený (source=coor).
  // Funguje i v mobilní appce — iOS/Android Mapy.cz si URL otevřou nativně.
  return `https://mapy.com/letecka?source=coor&id=${lon}%2C${lat}&x=${lon}&y=${lat}&z=19`;
}

export default function Properties() {
  return (
    <Routes>
      <Route index element={<PropertiesList />} />
      <Route path=":id" element={<PropertyDetail />} />
    </Routes>
  );
}

const FILTER_FIELDS = [
  { key: 'obec', label: 'Obec', width: 'flex-1' },
  { key: 'ku', label: 'Katastrální území', width: 'flex-1' },
  { key: 'ulice', label: 'Ulice', width: 'flex-1' },
  { key: 'cp', label: 'Č. p.', width: 'w-24' },
  { key: 'parcela', label: 'Parcela', width: 'w-32' },
  { key: 'lv', label: 'LV', width: 'w-20' },
] as const;

function PropertiesList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [enrollOpen, setEnrollOpen] = useState(false);

  // Lokální stav pro inputy — synchronizujeme s URL až po Apply / Enter,
  // ať se neposíláme do API při každém stisku klávesy.
  const [draft, setDraft] = useState<Record<string, string>>(() =>
    Object.fromEntries(FILTER_FIELDS.map((f) => [f.key, searchParams.get(f.key) || ''])),
  );

  // Aktivní filtry pocházejí čistě z URL.
  const filters = useMemo(() => {
    const out: Record<string, string> = {};
    for (const f of FILTER_FIELDS) {
      const v = searchParams.get(f.key);
      if (v) out[f.key] = v;
    }
    const status = searchParams.get('status');
    if (status) out.status = status;
    const letterAge = searchParams.get('letterAge');
    if (letterAge) out.letterAge = letterAge;
    return out;
  }, [searchParams]);

  // Pokud uživatel klikne v navigaci na "Nemovitosti" znovu, URL se vyčistí,
  // ale draft v inputech by zůstal → synchronizujeme zpět.
  useEffect(() => {
    setDraft((prev) => {
      const next = { ...prev };
      for (const f of FILTER_FIELDS) {
        const url = searchParams.get(f.key) || '';
        if (url === '' && prev[f.key]) next[f.key] = '';
      }
      return next;
    });
  }, [searchParams]);

  const query = useQuery({
    queryKey: ['properties', filters],
    queryFn: () => {
      const qs = new URLSearchParams(filters).toString();
      return api<PropertyListResponse>(`/api/properties${qs ? '?' + qs : ''}`);
    },
  });

  function apply() {
    const next = new URLSearchParams();
    const status = searchParams.get('status');
    const letterAge = searchParams.get('letterAge');
    if (status) next.set('status', status);
    if (letterAge) next.set('letterAge', letterAge);
    for (const [k, v] of Object.entries(draft)) {
      if (v.trim()) next.set(k, v.trim());
    }
    setSearchParams(next);
  }

  function clearAll() {
    setDraft(Object.fromEntries(FILTER_FIELDS.map((f) => [f.key, ''])));
    const next = new URLSearchParams();
    const status = searchParams.get('status');
    const letterAge = searchParams.get('letterAge');
    if (status) next.set('status', status);
    if (letterAge) next.set('letterAge', letterAge);
    setSearchParams(next);
  }

  const items = query.data?.items || [];

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">Nemovitosti</h1>
        <Link
          to="/upload"
          className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm font-medium"
        >
          + Přidat
        </Link>
      </header>

      <div className="flex gap-1 text-sm border-b">
        {STATUS_FILTERS.map((s) => {
          const isActive = (searchParams.get('status') || '') === s.value;
          return (
            <button
              key={s.value || 'all'}
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                if (s.value) next.set('status', s.value);
                else next.delete('status');
                setSearchParams(next);
              }}
              className={cn(
                'px-3 py-2 -mb-px border-b-2 transition-colors',
                isActive
                  ? 'border-foreground text-foreground font-medium'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {s.label}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        <span className="text-muted-foreground uppercase tracking-wide mr-1">
          Poslední dopis
        </span>
        {LETTER_AGE_FILTERS.map((f) => {
          const isActive = (searchParams.get('letterAge') || '') === f.value;
          return (
            <button
              key={f.value || 'all'}
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                if (f.value) next.set('letterAge', f.value);
                else next.delete('letterAge');
                setSearchParams(next);
              }}
              className={cn(
                'px-2 py-0.5 rounded border transition-colors',
                isActive
                  ? 'bg-foreground text-background border-foreground'
                  : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/40',
              )}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          apply();
        }}
        className="rounded-lg border p-3 flex flex-wrap items-end gap-2 bg-muted/20"
      >
        {FILTER_FIELDS.map((f) => (
          <div key={f.key} className={cn('flex flex-col', f.width)}>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
              {f.label}
            </label>
            <input
              type="text"
              value={draft[f.key] || ''}
              onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
              className="rounded-md border bg-background px-2 py-1.5 text-sm"
            />
          </div>
        ))}
        <button
          type="submit"
          className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm font-medium"
        >
          Filtrovat
        </button>
        {Object.keys(filters).length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="px-3 py-1.5 rounded-md border text-sm hover:bg-muted"
          >
            Vyčistit
          </button>
        )}
      </form>

      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2 w-8">
                <input
                  type="checkbox"
                  checked={items.length > 0 && items.every((p) => selectedIds.has(p.id))}
                  onChange={(e) => {
                    if (e.target.checked) setSelectedIds(new Set(items.map((p) => p.id)));
                    else setSelectedIds(new Set());
                  }}
                  aria-label="Vybrat všechny"
                />
              </th>
              <th className="text-left px-3 py-2 font-medium w-14"></th>
              <th className="text-left px-3 py-2 font-medium">Obec / Adresa / GPS</th>
              <th className="text-left px-3 py-2 font-medium">Parcela</th>
              <th className="text-left px-3 py-2 font-medium">LV</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="text-left px-3 py-2 font-medium">Flagy</th>
              <th className="text-right px-3 py-2 font-medium">Poslední dopis</th>
              <th className="text-right px-3 py-2 font-medium">Přidáno</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {query.isLoading && (
              <tr>
                <td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">
                  Načítám…
                </td>
              </tr>
            )}
            {query.error && (
              <tr>
                <td colSpan={9} className="px-3 py-8 text-center text-destructive">
                  Chyba: {String(query.error)}
                </td>
              </tr>
            )}
            {!query.isLoading && items.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">
                  Žádné nemovitosti.{' '}
                  <Link to="/upload" className="underline">
                    Přidej první fotku.
                  </Link>
                </td>
              </tr>
            )}
            {items.map((p) => (
              <PropertyRow
                key={p.id}
                p={p}
                selected={selectedIds.has(p.id)}
                onToggle={() =>
                  setSelectedIds((prev) => {
                    const next = new Set(prev);
                    if (next.has(p.id)) next.delete(p.id);
                    else next.add(p.id);
                    return next;
                  })
                }
              />
            ))}
          </tbody>
        </table>
      </div>

      {items.length > 0 && (
        <div className="text-xs text-muted-foreground text-right">{items.length} záznamů</div>
      )}

      {selectedIds.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3 rounded-full border shadow-lg bg-background px-4 py-2 text-sm">
          <span className="font-medium">Vybráno {selectedIds.size}</span>
          <button
            onClick={() => setEnrollOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-medium"
          >
            <PaperPlaneTilt size={12} />
            Přidat do kampaně
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Zrušit výběr
          </button>
        </div>
      )}

      {enrollOpen && (
        <EnrollModal
          propertyIds={Array.from(selectedIds)}
          onClose={() => setEnrollOpen(false)}
          onDone={() => {
            setSelectedIds(new Set());
            setEnrollOpen(false);
          }}
        />
      )}
    </div>
  );
}

function EnrollModal({
  propertyIds,
  onClose,
  onDone,
}: {
  propertyIds: string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [campaignId, setCampaignId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ enrolled: string[]; skipped_existing: string[]; skipped_not_found: string[] } | null>(null);

  const campaignsQuery = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => api<{ ok: true; items: Campaign[] }>('/api/campaigns'),
  });
  const campaigns = (campaignsQuery.data?.items || []).filter((c) => c.active);

  if (campaigns.length > 0 && !campaignId) setCampaignId(campaigns[0].id);

  const enroll = useMutation({
    mutationFn: () =>
      api<{ ok: true; enrolled: string[]; skipped_existing: string[]; skipped_not_found: string[] }>(
        `/api/campaigns/${campaignId}/enroll`,
        { method: 'POST', json: { propertyIds } },
      ),
    onSuccess: (data) => {
      setResult({ enrolled: data.enrolled, skipped_existing: data.skipped_existing, skipped_not_found: data.skipped_not_found });
      qc.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (e: unknown) => {
      const msg =
        e instanceof ApiError
          ? `${e.message}${typeof e.body === 'object' && e.body && 'message' in e.body ? ' — ' + (e.body as { message?: string }).message : ''}`
          : e instanceof Error
            ? e.message
            : String(e);
      setError(msg);
    },
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-background rounded-lg border shadow-lg w-full max-w-md">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h2 className="text-sm font-semibold">Přidat do kampaně</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-sm">
            ✕
          </button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-sm text-muted-foreground">
            {propertyIds.length} {propertyIds.length === 1 ? 'nemovitost' : 'nemovitostí'} bude přidáno do kampaně.
            Scheduler začne posílat dopisy podle nastavených kroků.
          </p>

          {campaigns.length === 0 ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              Žádná aktivní kampaň. <Link to="/campaigns/new" className="underline">Vytvořit</Link>.
            </div>
          ) : (
            <select
              value={campaignId}
              onChange={(e) => setCampaignId(e.target.value)}
              className="w-full rounded-md border bg-background px-2.5 py-1.5 text-sm"
              disabled={!!result}
            >
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.steps.length} {c.steps.length === 1 ? 'krok' : 'kroků'})
                </option>
              ))}
            </select>
          )}

          {error && (
            <div className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          {result && (
            <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-1">
              <div>
                ✓ Přidáno: <strong>{result.enrolled.length}</strong>
              </div>
              {result.skipped_existing.length > 0 && (
                <div className="text-muted-foreground">
                  Už v kampani: {result.skipped_existing.length}
                </div>
              )}
              {result.skipped_not_found.length > 0 && (
                <div className="text-destructive">
                  Nenalezené: {result.skipped_not_found.length}
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 pt-2">
            {result ? (
              <button
                onClick={onDone}
                className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium"
              >
                Hotovo
              </button>
            ) : (
              <button
                onClick={() => enroll.mutate()}
                disabled={!campaignId || enroll.isPending}
                className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50"
              >
                {enroll.isPending ? 'Přidávám…' : 'Přidat'}
              </button>
            )}
            <button onClick={onClose} className="px-4 py-2 rounded-md border text-sm hover:bg-muted">
              Zrušit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PropertyRow({
  p,
  selected,
  onToggle,
}: {
  p: PropertyListItem;
  selected: boolean;
  onToggle: () => void;
}) {
  const isUnclassified = p.status === 'unclassified';
  const hasGps = p.gpsLat != null && p.gpsLon != null;
  const [uploadOpen, setUploadOpen] = useState(false);
  return (
    <tr className={cn('hover:bg-muted/30 align-top', selected && 'bg-primary/5')}>
      <td className="px-3 py-2.5">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          aria-label="Vybrat řádek"
        />
      </td>
      <td className="px-3 py-2.5">
        {p.photoFileId ? (
          <Link to={`/properties/${p.id}`} className="block">
            <img
              src={`/api/photos/${p.photoFileId}`}
              alt="fotka z terénu"
              className="w-12 h-12 object-cover rounded border bg-muted"
              loading="lazy"
            />
          </Link>
        ) : (
          <div className="w-12 h-12 rounded border bg-muted/40 flex items-center justify-center">
            <span className="text-[10px] text-muted-foreground">PDF</span>
          </div>
        )}
      </td>
      <td className="px-3 py-2.5">
        {isUnclassified ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            <Link
              to={`/properties/${p.id}`}
              className="font-medium hover:underline text-muted-foreground italic"
            >
              Nezatříděná nemovitost
            </Link>
            {hasGps && (
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                <MapPin size={11} className="shrink-0" />
                <span className="font-mono tabular-nums">
                  {p.gpsLat!.toFixed(5)}, {p.gpsLon!.toFixed(5)}
                </span>
              </span>
            )}
            {hasGps && (
              <a
                href={mapyUrl(p.gpsLat!, p.gpsLon!)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-0.5 text-primary hover:underline"
              >
                mapy.com
                <ArrowSquareOut size={9} />
              </a>
            )}
            <button
              onClick={() => setUploadOpen(true)}
              className="inline-flex items-center gap-0.5 text-primary hover:underline"
            >
              <FilePlus size={11} />
              katastr
            </button>
            {p.gpsCapturedAt && (
              <span className="text-[10px] text-muted-foreground/70 ml-auto">
                {new Date(p.gpsCapturedAt).toLocaleString('cs-CZ', { dateStyle: 'short', timeStyle: 'short' })}
              </span>
            )}
            {uploadOpen && (
              <KatastrUploadModal
                propertyId={p.id}
                title={hasGps ? `${p.gpsLat!.toFixed(5)}, ${p.gpsLon!.toFixed(5)}` : undefined}
                onClose={() => setUploadOpen(false)}
              />
            )}
          </div>
        ) : (
          <div className="space-y-0.5">
            <Link to={`/properties/${p.id}`} className="font-medium hover:underline">
              {p.obec || '—'}
            </Link>
            {p.ku && p.ku !== p.obec && (
              <div className="text-xs text-muted-foreground">k. ú. {p.ku}</div>
            )}
            {p.ulice && (
              <div className="text-xs text-muted-foreground">
                {p.ulice} {p.cp || ''}
              </div>
            )}
          </div>
        )}
      </td>
      <td className="px-3 py-2.5 font-mono text-xs">{p.parcela || '—'}</td>
      <td className="px-3 py-2.5 font-mono text-xs">{p.lv || '—'}</td>
      <td className="px-3 py-2.5">
        <StatusPill status={p.status} />
      </td>
      <td className="px-3 py-2.5">
        <div className="flex flex-wrap gap-1">
          {p.flags.map((f) => (
            <span
              key={f}
              className={cn(
                'px-1.5 py-0.5 rounded text-[10px] font-medium border',
                DISTRESS_FLAGS.has(f)
                  ? 'bg-destructive/10 border-destructive/40 text-destructive'
                  : 'bg-amber-100 border-amber-300 text-amber-900',
              )}
            >
              {FLAG_LABELS[f] || f}
            </span>
          ))}
        </div>
      </td>
      <td className="px-3 py-2.5 text-right text-xs text-muted-foreground tabular-nums">
        {p.lastLetterAt ? formatLetterAge(p.lastLetterAt) : <span className="italic">—</span>}
      </td>
      <td className="px-3 py-2.5 text-right text-xs text-muted-foreground tabular-nums">
        {new Date(p.createdAt).toLocaleDateString('cs-CZ')}
      </td>
    </tr>
  );
}

function formatLetterAge(ms: number): string {
  const days = Math.floor((Date.now() - ms) / 86400_000);
  if (days < 1) return 'dnes';
  if (days < 2) return 'včera';
  if (days < 30) return `před ${days} dny`;
  const months = Math.floor(days / 30);
  if (months < 12) return `před ${months} měs.`;
  return new Date(ms).toLocaleDateString('cs-CZ');
}

function StatusPill({ status }: { status: PropertyStatus }) {
  const cls =
    status === 'unclassified'
      ? 'bg-amber-100 border-amber-300 text-amber-900'
      : status === 'parcel_resolved'
        ? 'bg-blue-100 border-blue-300 text-blue-900'
        : status === 'ready'
          ? 'bg-green-100 border-green-300 text-green-900'
          : status === 'archived'
            ? 'bg-muted border-border text-muted-foreground'
            : 'bg-muted/60 border-border text-foreground';
  return (
    <span className={cn('inline-block px-1.5 py-0.5 rounded text-[10px] font-medium border whitespace-nowrap', cls)}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}
